/**
 * useExport — the member data-export flow (033 US5, T041).
 *
 * Two layers, deliberately separated so the state machine is testable
 * without a DOM download:
 *
 *   - `useExport()` — the mutation + phase state machine. Reads the
 *     `myDataExport` port from PortsContext and exposes
 *     `{ phase, start, download, reset }`.
 *   - `downloadArtifact()` — the DOM side effect (Blob + object URL +
 *     synthetic anchor click). Exported so the ready state's affordance
 *     can call it, and so a test can call it directly.
 *
 * READY SIGNAL, not an auto-download
 * ==================================
 *
 * The flow ends in an explicit READY phase carrying both artifacts, and
 * the member chooses which to download. It does not fire a download the
 * moment the promise resolves. Two reasons:
 *
 *   1. FR-014 ships TWO renderings (machine-readable JSON + a
 *      human-readable text rendering). Auto-downloading would have to
 *      pick one for the member, or fire two downloads at once — browsers
 *      treat the second as a popup-ish action and may block it.
 *   2. A programmatic download not tied to a user gesture is the pattern
 *      browsers throttle hardest. Keeping the click inside the ready
 *      state means the download always originates from a real gesture.
 *
 * Consumers re-render on phase change, which is the only signal they need.
 *
 * This is also the seam where the deferred async path lands: when the API
 * grows an out-of-band ready signal (needs an object-storage owner
 * outside the 033 module — see MyDataExportPort's shape note), `phase`
 * gains a `pending`-with-poll arm and the ready arm is unchanged.
 *
 * Owned by feature 033-data-ownership-and-lifecycle-foundations.
 * Contract: specs/033-.../contracts/my-data-api.md.
 */

import { useContext, useRef, useState } from 'react';

import { PortsContext } from '../providers/PortsContext.js';
import type { MyDataExportArtifact, MyDataExportResult } from './MyDataExportPort.js';

/**
 * The flow's single discriminant — no orthogonal booleans (the
 * DeleteAccountConfirm `phase` posture).
 *
 *   idle    — nothing requested yet
 *   pending — the export is in flight; the trigger disables
 *   ready   — both artifacts are in hand; the member downloads either
 *   error   — the request failed; the member can retry
 *
 * There is deliberately NO `unavailable` arm. The erasure flow has one because
 * its API can refuse for a real business reason (PRECONDITION_FAILED while the
 * retention parameter is unconfigured). The export needs no such parameter, so
 * it has no refusal code: the only way `member.myData` can be missing is a
 * router composed without the namespace, and this repo deploys web and api
 * together, which makes that a loud composition bug rather than a member-facing
 * state (see TrpcMyDataExportAdapter). An arm no production path can reach
 * would be dead code plus dead member-facing copy.
 */
export type ExportPhase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'pending' }
  | { readonly kind: 'ready'; readonly result: MyDataExportResult }
  | { readonly kind: 'error' };

export interface ExportFlow {
  readonly phase: ExportPhase;
  /** Request the export. A no-op while one is already in flight. */
  readonly start: () => Promise<void>;
  /** Materialize one artifact as a file download. */
  readonly download: (artifact: MyDataExportArtifact) => void;
  /** Return to idle (e.g. when the member dismisses the ready state). */
  readonly reset: () => void;
}

/**
 * Materialize an inline artifact as a browser download. The artifacts come
 * back inline rather than as an object-storage reference (see
 * MyDataExportPort's shape note), so the client builds the file itself.
 *
 * The object URL is revoked after the click: without it every export leaks the
 * artifact's full contents for the lifetime of the document. The revoke is
 * DEFERRED a macrotask rather than run in the same tick as `click()`, because
 * some browsers cancel a still-pending download when the blob URL is revoked
 * synchronously alongside the click, which would silently produce no file.
 * `MembershipCertificateSection` established this posture for the same reason;
 * this matches it rather than contradicting it.
 *
 * The scheduling lives in a `finally` so a throwing `click()` still releases
 * the URL instead of leaking it.
 */
export function downloadArtifact(artifact: MyDataExportArtifact): void {
  const blob = new Blob([artifact.contents], { type: artifact.mimeType });
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = artifact.filename;
    // Not appended to the document: a detached anchor's synthetic click is
    // honoured by every browser we target, and it cannot disturb layout or
    // leave a stray node behind if the click throws.
    anchor.click();
  } finally {
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 0);
  }
}

export function useExport(): ExportFlow {
  const ports = useContext(PortsContext);

  if (ports === null) {
    throw new Error(
      'useExport: PortsContext not found. Make sure the component is wrapped in a PortsProvider.',
    );
  }

  if (ports.myDataExport === undefined) {
    throw new Error(
      'useExport: myDataExport port is not registered. This feature requires the my-data-export port to be configured.',
    );
  }

  const port = ports.myDataExport;
  const [phase, setPhase] = useState<ExportPhase>({ kind: 'idle' });
  // Double-submit guard. A ref, not the `phase` state: the guard must be
  // correct at CALL time, and a state read would either be stale in the
  // callback's closure or depend on React's setState-updater timing. Each
  // export writes an audit event server-side, so a duplicate is not free.
  const inFlight = useRef(false);

  // No manual memoization (R48): nothing here is used as an effect dependency
  // — `start` and `reset` are invoked from click handlers and `download` is a
  // module-level function — so a stable identity would buy nothing and would
  // need a `react-compiler-exception` justification it does not have.
  async function start(): Promise<void> {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    setPhase({ kind: 'pending' });
    try {
      const result = await port.requestExport();
      setPhase({ kind: 'ready', result });
    } catch {
      setPhase({ kind: 'error' });
    } finally {
      inFlight.current = false;
    }
  }

  function reset(): void {
    setPhase({ kind: 'idle' });
  }

  return { phase, start, download: downloadArtifact, reset };
}
